/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { createDataActionHook, IActionContext, IActionInput, IMSDyn365Window } from '@msdyn365-commerce/core';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { IProductInfo, IProductTransaction, TelemetryEvent } from '@msdyn365-commerce/telemetry-internal';
import { getProductInfoFromCartLinesAsync } from './telemetry.action.helper';

declare let window: IMSDyn365Window;

/**
 * Checkout data action pre-hook
 * @param inputs Data action inputs
 */
const beforePurchase = async (inputs: IActionInput | IActionInput[]) => {
    // placeholder
};

/**
 * Fires a Purchase event after Checkout data action completed
 * @param _inputs Data action inputs
 * @param salesOrder Sales order for transaction
 * @param actionContext Action context
 */
const afterPurchase = async (
    _inputs: IActionInput | IActionInput[],
    salesOrder: SalesOrder | SalesOrder[],
    actionContext: IActionContext
) => {
    if (window && window._msdyn365 && window._msdyn365.telemetry) {
        const salesObject = Array.isArray(salesOrder) ? salesOrder[0] : salesOrder;
        let saleLineProductUnits: IProductInfo[] = [];

        // Collect info on products in the sales order
        if (salesObject && salesObject.SalesLines && Array.isArray(salesObject.SalesLines) && salesObject.SalesLines.length > 0) {
            try {
                saleLineProductUnits = await getProductInfoFromCartLinesAsync(salesObject.SalesLines, actionContext);
            } catch (error) {
                actionContext.telemetry.error('Checkout post-data action hook failed to process cart lines.', error);
            }
        }

        // Store the purchased product information so that we can avoid "code triggered" remove from cart telemetry event.
        actionContext.requestContext.telemetryData[TelemetryEvent.Purchase] = {
            id: salesObject.Id,
            affiliation: salesObject.ChannelId ? salesObject.ChannelId.toString() : '',
            revenue: salesObject.NetAmountWithoutTax,
            tax: salesObject.TaxAmount,
            // shippingCost?: // Confirm if it is delivery mode charge amount;
            products: saleLineProductUnits
        };

        // Fire Purchase event
        window._msdyn365.telemetry.logEvent(
            TelemetryEvent.Purchase,
            <IProductTransaction>actionContext.requestContext.telemetryData[TelemetryEvent.Purchase]
        );
    }
};

createDataActionHook({
    actionId: '@msdyn365-commerce/retail-proxy/Carts/Checkout',
    postReaderHook: afterPurchase,
    preReaderHook: beforePurchase
});
